import { useState, useEffect, useRef } from 'react'

/**
 * Hook for debouncing a value - useful for search inputs
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds
 * @returns The debounced value
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = window.setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      window.clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

/**
 * Hook for debouncing a callback function
 * @param callback - The function to debounce
 * @param delay - Delay in milliseconds
 * @returns Object with debouncedCallback, cancel and flush functions
 */
export function useDebouncedCallback<TArgs extends any[]>(
  callback: (...args: TArgs) => void,
  delay: number
) {
  const callbackRef = useRef(callback)
  const timeoutRef = useRef<number>()

  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  const debouncedCallback = (...args: TArgs) => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = window.setTimeout(() => {
      callbackRef.current(...args)
    }, delay)
  }

  const cancel = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = undefined
    }
  }

  const flush = (...args: TArgs) => {
    cancel()
    callbackRef.current(...args)
  }

  // Cleanup on unmount
  useEffect(() => {
    return cancel
  }, [])

  return {
    debouncedCallback,
    cancel,
    flush,
  }
}

/**
 * Hook for debounced search functionality
 * @param searchFn - Function to execute the search
 * @param delay - Debounce delay in milliseconds
 * @returns Search state and actions
 */
export function useDebouncedSearch<T>(
  searchFn: (query: string) => Promise<T[]>,
  delay: number = 300
) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<T[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const debouncedQuery = useDebounce(query, delay)

  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults([])
      setError(null)
      return
    }

    const executeSearch = async () => {
      setIsSearching(true)
      setError(null)

      try {
        const searchResults = await searchFn(debouncedQuery)
        setResults(searchResults)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erreur de recherche'
        setError(errorMessage)
        setResults([])
      } finally {
        setIsSearching(false)
      }
    }

    executeSearch()
  }, [debouncedQuery, searchFn])

  const clearSearch = () => {
    setQuery('')
    setResults([])
    setError(null)
  }

  return {
    query,
    setQuery,
    results,
    isSearching,
    error,
    clearSearch,
    debouncedQuery,
  }
}